import {
  getImageOrigin,
  type ImageLayer,
  type Layer,
} from "@hightouch/lib/ad-studio/html-editor/model";
import { parseDoc } from "@hightouch/lib/ad-studio/html-editor/parse";

/** Flatten the layer tree (groups nest children) down to its image layers. */
export function collectImageLayers(layers: Layer[]): ImageLayer[] {
  const images: ImageLayer[] = [];
  for (const layer of layers) {
    if (layer.type === "image") {
      images.push(layer);
    } else if (layer.type === "group") {
      images.push(...collectImageLayers(layer.children));
    }
  }
  return images;
}

export type AdMediaInput = {
  /** The ad's rendered markup (HTML or SVG). */
  content: string;
  /** Component-asset map (relativePath → S3 key) needed to recover asset keys. */
  componentAssets: Record<string, string> | null;
  /** API base used to resolve `ad:`/`/agents/files?key=…` URLs at parse time. */
  apiBase: string;
};

/**
 * Placeholder for "does this ad contain AI-generated media": parse the markup
 * and check whether any image layer resolves to a generated source via
 * `getImageOrigin`. To be replaced by the authoritative `component_asset_sources`
 * provenance signal (ADS-462) once that ships.
 *
 * `getImageOrigin` only recovers an `assetKey` when `parseDoc` is given both
 * `componentAssets` and `apiBase`, so both must be passed. Note: SVG-rooted ads
 * keep their images inside opaque SVG markup (no per-image layers), so this
 * under-reports for those — acceptable for a placeholder.
 */
export function adHasAiGeneratedMedia({
  content,
  componentAssets,
  apiBase,
}: AdMediaInput): boolean {
  let layers: Layer[];
  try {
    layers = parseDoc(content, { componentAssets, apiBase }).layers;
  } catch {
    return false;
  }
  return collectImageLayers(layers).some(
    (layer) => getImageOrigin(layer) === "generated",
  );
}
